import React, { forwardRef, useEffect, useState } from 'react';
import { Button } from '@/components/button';
import { MicIcon, SendIcon, StopCircleIcon, LoaderIcon } from 'lucide-react';
import { Textarea } from '../textarea';
import { useVoiceRecording } from '@/hooks/useVoiceRecording';

interface InputAreaProps {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleSend: () => void;
  isListening: boolean;
  setIsListening: React.Dispatch<React.SetStateAction<boolean>>;
}

const InputArea = forwardRef<HTMLTextAreaElement, InputAreaProps>(
  ({ input, setInput, handleSend, isListening, setIsListening }, ref) => {
    const [textareaHeight, setTextareaHeight] = useState<number>(52);
    const minHeight = 58;
    const maxHeight = 400;

    const keyboardTimer = React.useRef<NodeJS.Timeout | null>(null);

    const {
      isListening: isRecording,
      isProcessing,
      isConnecting,
      audioLevel,
      startRecording,
      stopRecording,
      cleanup,
    } = useVoiceRecording({
      handleSend: handleSend,
      onTranscriptReceived: (transcript) => {
        setInput((prevInput) => prevInput + ' ' + transcript);
      },
      silenceThreshold: 40, // Optional: Configure silence threshold (0-255)
      silenceDuration: 3000, // Optional: Configure silence duration (ms)
    });

    const handleKeyboardActivity = () => {
      // Clear any existing timer
      if (keyboardTimer.current) {
        clearTimeout(keyboardTimer.current);
      }

      // Set new timer to hide keyboard after 10 seconds
      keyboardTimer.current = setTimeout(() => {
        if (ref && 'current' in ref && ref.current) {
          ref.current.blur();
        }
      }, 10000); // 10 seconds
    };

    useEffect(() => {
      return () => {
        if (keyboardTimer.current) {
          clearTimeout(keyboardTimer.current);
        }
      };
    }, []);

    // Update parent component's isListening state
    useEffect(() => {
      setIsListening(isRecording);
    }, [isRecording, setIsListening]);

    const toggleListening = () => {
      logBrowserInfo();
      const compatibility = checkBrowserCompatibility();
      if (!compatibility.isCompatible) {
        alert(compatibility.message);
        return;
      }

      if (compatibility.message) {
        console.log(compatibility.message);
      }

      if (isRecording && !isProcessing) {
        stopRecording();
      } else if (!isRecording) {
        startRecording();
      }
    };

    const handleTextareaChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      setInput(e.target.value);
      adjustTextareaHeight(e.target);
      handleKeyboardActivity();
    };

    const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
      element.style.height = `${minHeight}px`;
      const newHeight = Math.min(
        Math.max(element.scrollHeight, minHeight),
        maxHeight
      );
      setTextareaHeight(newHeight);
      element.style.height = `${newHeight}px`;
    };

    useEffect(() => {
      if (ref && 'current' in ref && ref.current) {
        adjustTextareaHeight(ref.current);
      }
    }, [input]);

    useEffect(() => {
      return () => {
        cleanup();
      };
    }, []);

    const checkBrowserCompatibility = () => {
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS && isSafari) {
        // Don't check mediaDevices yet for iOS Safari
        return {
          isCompatible: true,
          message: 'Please allow microphone access when prompted.',
        };
      }

      // For other browsers, we can check mediaDevices
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          isCompatible: false,
          message: 'Voice recording is not supported in this browser.',
        };
      }

      return { isCompatible: true, message: null };
    };

    // Add this debugging function
    const logBrowserInfo = () => {
      console.log('Browser Information:');
      console.log('UserAgent:', navigator.userAgent);
      console.log('mediaDevices available:', !!navigator.mediaDevices);
      console.log(
        'getUserMedia available:',
        !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      );
      console.log('Platform:', navigator.platform);
      console.log('Vendor:', navigator.vendor);
    };

    return (
      <div className="relative bg-[#1e1e1e] rounded-md">
        <Textarea
          ref={ref}
          placeholder={isListening ? 'Listening...' : 'Ask anything...'}
          value={input}
          onChange={handleTextareaChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
            handleKeyboardActivity();
          }}
          onFocus={handleKeyboardActivity}
          className="w-full max-w-full bg-transparent text-white !ring-0 placeholder-gray-500 text-lg active:ring-0 active:border-[#e66b3c] active:outline-none focus:ring-0 focus:outline-none focus:border-[#e66b3c] pr-24 resize-none py-4 leading-[1.5] custom-scrollbar"
          style={{
            height: `${textareaHeight}px`,
            minHeight: `${minHeight}px`,
            maxHeight: `${maxHeight}px`,
            overflowY: textareaHeight > maxHeight - 4 ? 'auto' : 'hidden',
          }}
        />
        <div className="absolute right-3 bottom-3 flex space-x-2 items-center">
          {isListening && !isProcessing && (
            <div className="flex items-center space-x-1">
              <div
                className="w-2 h-4 bg-red-500 rounded-full animate-pulse"
                style={{ opacity: 0.3 + (audioLevel / 255) * 0.7 }}
              />
              <div
                className="w-2 h-4 bg-red-500 rounded-full animate-pulse"
                style={{
                  opacity: 0.3 + (audioLevel / 255) * 0.7,
                  animationDelay: '0.2s',
                }}
              />
              <div
                className="w-2 h-4 bg-red-500 rounded-full animate-pulse"
                style={{
                  opacity: 0.3 + (audioLevel / 255) * 0.7,
                  animationDelay: '0.4s',
                }}
              />
            </div>
          )}
          {(isProcessing || isConnecting) && (
            <LoaderIcon className="h-6 w-6 animate-spin text-[#4A9BFF]" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleListening}
            disabled={isProcessing || isConnecting}
            className="text-white hover:text-[#4A9BFF] transition-all duration-300"
          >
            {isListening ? (
              <StopCircleIcon className="h-6 w-6" />
            ) : (
              <MicIcon className="h-6 w-6" />
            )}
          </Button>
          <Button
            onClick={handleSend}
            className="bg-transparent hover:bg-transparent text-white hover:text-[#4A9BFF]"
          >
            <SendIcon className="h-6 w-6" />
          </Button>
        </div>
      </div>
    );
  }
);

InputArea.displayName = 'InputArea';

export { InputArea };
